import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n, catName } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import SmartSearchBar from "@/components/SmartSearchBar";
import ProductCard from "@/components/ProductCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { fileUrl } from "@/lib/api";
import * as Icons from "lucide-react";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Home() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recs, setRecs] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [newest, setNewest] = useState([]);

  useEffect(() => {
    api.get("/products?sort=popular&limit=8").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/sellers/featured").then((r) => setSellers(r.data)).catch(() => {});
    api.get("/products?sort=recent&limit=8").then((r) => setNewest(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/recommendations/home").then((r) => {
        if (r.data.personalized && r.data.blocks.length) setRecs(r.data.blocks);
      }).catch(() => {});
    } else setRecs(null);
  }, [user]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute top-20 -left-40 h-80 w-80 rounded-full bg-amber-100/50 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-5 pt-20 pb-16 text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0EA5E9] bg-sky-50 border border-sky-100 rounded-full px-4 py-2"
          >
            <Sparkles className="h-3.5 w-3.5" /> {t("home.badge")}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F172A] mt-6 leading-[1.05] max-w-4xl mx-auto"
          >
            {t("home.title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }}
            className="text-[#475569] text-base md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed"
          >
            {t("home.subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-9 max-w-2xl mx-auto"
          >
            <SmartSearchBar big />
            <p className="text-xs text-[#94A3B8] mt-3 flex items-center justify-center gap-1.5">
              <Sparkles className="h-3 w-3 text-[#0EA5E9]" /> {t("home.aiHint")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Recommendations for logged-in buyers */}
      {recs && (
        <section className="max-w-7xl mx-auto px-5 py-6">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-[#0EA5E9]" />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A]">{t("home.forYou")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {recs.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A] mb-6">{t("home.byCategory")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => {
            const Icon = Icons[c.icon ? c.icon.split("-").map((s, idx) => idx === 0 ? s[0].toUpperCase() + s.slice(1) : s[0].toUpperCase() + s.slice(1)).join("") : "Box"] || Icons.Box;
            return (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}>
                <Link to={`/explore?category=${c.slug}`} data-testid={`home-cat-${c.slug}`}
                  className="group flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:border-[#0EA5E9] hover:-translate-y-0.5 transition-[transform,border-color]">
                  <div className="h-11 w-11 rounded-xl bg-sky-50 text-[#0EA5E9] flex items-center justify-center group-hover:bg-[#0EA5E9] group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-display font-bold text-[#0F172A]">{catName(c, lang)}</span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Featured verified sellers */}
      {sellers.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 py-6">
          <div className="flex items-center gap-2 mb-6">
            <VerifiedBadge size={22} />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A]">{t("home.featuredSellers")}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {sellers.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={`/seller/${s.id}`} data-testid={`featured-seller-${s.id}`}
                  className="group flex flex-col items-center text-center bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:border-[#0EA5E9] hover:-translate-y-1 transition-[transform,border-color]">
                  {s.avatar_url ? (
                    <img src={fileUrl(s.avatar_url)} alt={s.display_name} className="h-16 w-16 rounded-2xl object-cover" />
                  ) : (
                    <div className="h-16 w-16 rounded-2xl bg-[#0F172A] text-white text-xl font-extrabold flex items-center justify-center">{s.display_name[0]}</div>
                  )}
                  <div className="flex items-center gap-1 mt-3">
                    <span className="font-display font-bold text-sm text-[#0F172A] truncate max-w-[110px]">{s.display_name}</span>
                    <VerifiedBadge size={13} />
                  </div>
                  <span className="text-[11px] text-[#94A3B8] mt-1">{s.total_sales} {t("seller.sales")}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Trending */}
      <section className="max-w-7xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A]">{t("home.trending")}</h2>          <Link to="/explore" className="text-sm font-semibold text-[#0EA5E9] flex items-center gap-1 hover:gap-2 transition-all" data-testid="see-all-link">
            {t("nav.explore")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* New arrivals */}
      {newest.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 py-6">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A]">{t("home.newArrivals")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="new-arrivals-grid">
            {newest.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Seller CTA */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] p-10 md:p-16 grain">
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#0EA5E9]/30 blur-3xl" />
          <div className="relative max-w-xl">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight">{t("home.ctaSellTitle")}</h2>
            <p className="text-slate-300 mt-4 leading-relaxed">{t("home.ctaSellText")}</p>
            <Link to="/register" data-testid="home-sell-cta"
              className="inline-flex items-center gap-2 mt-7 bg-[#0EA5E9] hover:bg-white hover:text-[#0F172A] text-white font-bold rounded-full px-7 py-3.5 transition-colors">
              {t("home.ctaSellBtn")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
